import {useTrans, UseTransReturn} from '@ui/i18n/use-trans';
import {shallowEqual} from '@ui/utils/shallow-equal';
import {Children, memo, ReactElement, useLayoutEffect, useRef} from 'react';
import {MetaTag} from './meta-tag';
import {TitleMetaTagChildren} from './static-page-title';
import {refreshDocumentTitle, setPageDocumentTitle} from './document-title';

export const helmetAttribute = 'data-be-helmet';
let helmetSequence = 0;
const helmetEntries = new Map<symbol, {sequence: number; tags: MetaTag[]}>();

interface HelmetProps {
  children?: ReactElement | ReactElement[];
  tags?: MetaTag[] | string;
}
export const Helmet = memo(({children, tags}: HelmetProps) => {
  const {trans} = useTrans();
  const instanceId = useRef(Symbol('helmet')).current;

  const resolvedTags = normalizeMetaTags(
    !tags && children ? mapChildrenToTags(children, trans) : tags || [],
  );

  useLayoutEffect(() => {
    helmetEntries.set(instanceId, {
      sequence: ++helmetSequence,
      tags: resolvedTags,
    });
    updateTags();

    return () => {
      helmetEntries.delete(instanceId);
      updateTags();
    };
  }, [instanceId, resolvedTags]);

  return null;
}, shallowEqual);

function mapChildrenToTags(
  children: ReactElement | ReactElement[],
  trans: UseTransReturn['trans'],
): MetaTag[] {
  return Children.map(children, child => {
    const childProps = child.props as any;
    switch (child.type) {
      case 'title':
        return {
          nodeName: 'title',
          _text: titleTagChildrenToString(childProps.children, trans),
        };
      case 'meta':
        return {...childProps, nodeName: 'meta'};
    }
  });
}

function titleTagChildrenToString(
  children: TitleMetaTagChildren,
  trans: UseTransReturn['trans'],
): string {
  if (children == null) return '';
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map(c => titleTagChildrenToString(c, trans)).join('');
  }
  if ('message' in children) {
    return trans(children);
  }
  return trans(children.props);
}

function removeOldTags() {
  document.head
    .querySelectorAll(
      'meta:not([data-keep]), script[type="application/ld+json"]:not([data-keep]), title, link[rel="canonical"]',
    )
    .forEach(tag => {
      document.head.removeChild(tag);
    });
}

function updateTags() {
  if (!helmetEntries.size) return;

  removeOldTags();
  mergeRegisteredTags().forEach(tag => {
    updateTag(tag);
  });
  refreshDocumentTitle();
}

function mergeRegisteredTags(): MetaTag[] {
  const entries = [...helmetEntries.values()].sort(
    (a, b) => a.sequence - b.sequence,
  );
  const merged: MetaTag[] = [];
  const tagIndexes = new Map<string, number>();

  entries.forEach(entry => {
    entry.tags.forEach(tag => {
      const key = getTagMergeKey(tag);
      const existingIndex = key ? tagIndexes.get(key) : undefined;
      if (existingIndex == null) {
        if (key) tagIndexes.set(key, merged.length);
        merged.push(tag);
      } else {
        merged[existingIndex] = tag;
      }
    });
  });

  return merged;
}

function getTagMergeKey(tag: MetaTag): string | null {
  if (tag.nodeName === 'title') return 'title';
  if (tag.nodeName === 'meta') {
    const metaName = tag.property || tag.name || tag.type;
    return metaName ? `meta:${metaName}` : null;
  }
  if (tag.nodeName === 'link' && tag.rel) return `link:${tag.rel}`;
  return null;
}

export function normalizeMetaTags(
  tags: MetaTag[] | string | null | undefined,
): MetaTag[] {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags !== 'string') return [];

  const template = document.createElement('template');
  template.innerHTML = tags;

  return Array.from(template.content.children).flatMap(element => {
    const nodeName = element.tagName.toLowerCase();
    if (!['meta', 'script', 'title', 'link'].includes(nodeName)) {
      return [];
    }

    const tag: Record<string, string> = {nodeName};
    for (const attribute of element.attributes) {
      tag[attribute.name] = attribute.value;
    }
    if (nodeName === 'title') {
      tag._text = element.textContent?.trim() || '';
    } else if (nodeName === 'script') {
      tag._text = element.textContent || '';
    }
    return [tag as unknown as MetaTag];
  });
}

function updateTag(tag: MetaTag) {
  // update title
  if (tag.nodeName === 'title') {
    if (typeof tag._text !== 'undefined') {
      setPageDocumentTitle(tag._text);
    }
    return;
  }

  // update <meta> tag
  const newElement = document.createElement(tag.nodeName);
  for (const key in tag) {
    const attribute = key as keyof MetaTag;
    if (attribute === 'nodeName') continue;
    if (attribute === '_text') {
      newElement.textContent =
        typeof tag._text === 'string' ? tag._text : JSON.stringify(tag._text);
    } else {
      const value = tag[attribute] == null ? '' : tag[attribute];
      newElement.setAttribute(attribute, value as string);
    }
  }

  newElement.setAttribute(helmetAttribute, 'true');
  document.head.prepend(newElement);
}
